import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { Currency, CurrencyAmount, Price, Rounding, Token } from 'eth-mainnet-few-sdk-core-2'
import {
  encodeSqrtRatioX96,
  FeeAmount,
  nearestUsableTick,
  Pool,
  Position,
  priceToClosestTick,
  TICK_SPACINGS,
  TickMath,
  tickToPrice,
} from 'eth-mainnet-few-v3-sdk-2'
import { getFewTokenFromOriginalToken } from 'eth-mainnet-universal-router-sdk-3'
import { usePool } from 'hooks/usePools'
import { useOriginalTokenOfFewToken, useWrappedTokenFromOriginal } from 'hooks/useWrappedToken'
import JSBI from 'jsbi'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { ReactNode, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { getTickToPrice } from 'utils/getTickToPrice'
import { unwrappedOriginToken } from 'utils/unwrappedToken'

import { BIG_INT_ZERO } from '../../../constants/misc'
import { PoolState } from '../../../hooks/usePools'
import { useCurrencyBalances } from '../../connection/hooks'
import { AppState } from '../../reducer'
import {
  Bound,
  Field,
  setFullRange,
  typeInput,
  typeLeftRangeInput,
  typeRightRangeInput,
  typeStartPriceInput,
} from './actions'
import { tryParseTick } from './utils'

export function useV3MintState(): AppState['mintV3'] {
  return useAppSelector((state) => state.mintV3)
}

export function useV3MintActionHandlers(noLiquidity: boolean | undefined): {
  onFieldAInput: (typedValue: string) => void
  onFieldBInput: (typedValue: string) => void
  onLeftRangeInput: (typedValue: string) => void
  onRightRangeInput: (typedValue: string) => void
  onStartPriceInput: (typedValue: string) => void
} {
  const dispatch = useAppDispatch()

  const onFieldAInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const onFieldBInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: noLiquidity === true }))
    },
    [dispatch, noLiquidity]
  )

  const [searchParams, setSearchParams] = useSearchParams()

  const onLeftRangeInput = useCallback(
    (typedValue: string) => {
      dispatch(typeLeftRangeInput({ typedValue }))
      const paramMinPrice = searchParams.get('minPrice')
      if (!paramMinPrice || (paramMinPrice && paramMinPrice !== typedValue)) {
        searchParams.set('minPrice', typedValue)
        setSearchParams(searchParams)
      }
    },
    [dispatch, searchParams, setSearchParams]
  )

  const onRightRangeInput = useCallback(
    (typedValue: string) => {
      dispatch(typeRightRangeInput({ typedValue }))
      const paramMaxPrice = searchParams.get('maxPrice')
      if (!paramMaxPrice || (paramMaxPrice && paramMaxPrice !== typedValue)) {
        searchParams.set('maxPrice', typedValue)
        setSearchParams(searchParams)
      }
    },
    [dispatch, searchParams, setSearchParams]
  )

  const onStartPriceInput = useCallback(
    (typedValue: string) => {
      dispatch(typeStartPriceInput({ typedValue }))
    },
    [dispatch]
  )

  return {
    onFieldAInput,
    onFieldBInput,
    onLeftRangeInput,
    onRightRangeInput,
    onStartPriceInput,
  }
}

export function useV3DerivedMintInfo(
  currencyA?: Currency,
  currencyB?: Currency,
  feeAmount?: FeeAmount,
  baseCurrency?: Currency,
  // override for existing position
  existingPosition?: Position
): {
  pool?: Pool | null
  poolState: PoolState
  ticks: { [bound in Bound]?: number | undefined }
  price?: Price<Token, Token>
  fewPrice?: Price<Token, Token>
  pricesAtTicks: {
    [bound in Bound]?: Price<Token, Token> | undefined
  }
  pricesAtLimit: {
    [bound in Bound]?: Price<Token, Token> | undefined
  }
  currencies: { [field in Field]?: Currency }
  originCurrencies: { [field in Field]?: Currency }
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  dependentField: Field
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  position?: Position
  noLiquidity?: boolean
  noFewLiquidity?: boolean
  errorMessage?: ReactNode
  invalidPool: boolean
  outOfRange: boolean
  invalidRange: boolean
  depositADisabled: boolean
  depositBDisabled: boolean
  invertPrice: boolean
  invertFewPrice: boolean
  ticksAtLimit: { [bound in Bound]?: boolean | undefined }
} {
  const { account } = useWeb3React()

  const { independentField, typedValue, leftRangeTypedValue, rightRangeTypedValue, startPriceTypedValue } =
    useV3MintState()

  const dependentField = independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A

  // currencies
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB,
    }),
    [currencyA, currencyB]
  )

  const originalTokenA = useOriginalTokenOfFewToken(currencyA?.wrapped)
  const originalTokenB = useOriginalTokenOfFewToken(currencyB?.wrapped)

  const originCurrencies: { [field in Field]?: Currency } = useMemo(() => {
    const unwrapOriginCurrencies = {
      [Field.CURRENCY_A]: unwrappedOriginToken(originalTokenA as Token),
      [Field.CURRENCY_B]: unwrappedOriginToken(originalTokenB as Token),
    }
    return {
      [Field.CURRENCY_A]: unwrapOriginCurrencies[Field.CURRENCY_A],
      [Field.CURRENCY_B]: unwrapOriginCurrencies[Field.CURRENCY_B],
    }
  }, [originalTokenA, originalTokenB])

  const increaseCurrencies = currencies

  // formatted with tokens
  const [tokenA, tokenB, baseToken] = useMemo(
    () => [currencyA?.wrapped, currencyB?.wrapped, baseCurrency?.wrapped],
    [currencyA, currencyB, baseCurrency]
  )

  const fewTokenA = useWrappedTokenFromOriginal(tokenA)
  const fewTokenB = useWrappedTokenFromOriginal(tokenB)
  const fewBaseToken = useWrappedTokenFromOriginal(baseToken)

  const [token0, token1] = useMemo(
    () =>
      tokenA && tokenB ? (tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]) : [undefined, undefined],
    [tokenA, tokenB]
  )
  const [fewToken0, fewToken1] = useMemo(
    () =>
      fewTokenA && fewTokenB
        ? fewTokenA.sortsBefore(fewTokenB)
          ? [fewTokenA, fewTokenB]
          : [fewTokenB, fewTokenA]
        : [undefined, undefined],
    [fewTokenA, fewTokenB]
  )

  // balances
  const balances = useCurrencyBalances(
    account ?? undefined,
    useMemo(() => [increaseCurrencies[Field.CURRENCY_A], increaseCurrencies[Field.CURRENCY_B]], [increaseCurrencies])
  )
  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]: balances[0],
    [Field.CURRENCY_B]: balances[1],
  }

  const fewCurrencyA = useWrappedTokenFromOriginal(increaseCurrencies[Field.CURRENCY_A]?.wrapped)
  const fewCurrencyB = useWrappedTokenFromOriginal(increaseCurrencies[Field.CURRENCY_B]?.wrapped)

  const fewCurrencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: fewCurrencyA,
      [Field.CURRENCY_B]: fewCurrencyB,
    }),
    [fewCurrencyA, fewCurrencyB]
  )

  // pool
  const [poolState, pool] = usePool(fewCurrencyA, fewCurrencyB, feeAmount)
  const noLiquidity = poolState === PoolState.NOT_EXISTS

  const [fewPoolState, fewPool] = usePool(fewCurrencyA, fewCurrencyB, feeAmount)
  // const noFewLiquidity = fewPoolState === PoolState.NOT_EXISTS

  // note to parse inputs in reverse
  const invertPrice = Boolean(baseToken && token0 && !baseToken.equals(token0))
  const invertFewPrice = Boolean(fewBaseToken && fewToken0 && !fewBaseToken.equals(fewToken0))

  // always returns the price with 0 as base token
  // const price: Price<Token, Token> | undefined = useMemo(() => {
  //   // if no liquidity use typed value
  //   if (noLiquidity) {
  //     const parsedQuoteAmount = tryParseCurrencyAmount(startPriceTypedValue, invertPrice ? token0 : token1)
  //     if (parsedQuoteAmount && token0 && token1) {
  //       const baseAmount = tryParseCurrencyAmount('1', invertPrice ? token1 : token0)
  //       const price =
  //         baseAmount && parsedQuoteAmount
  //           ? new Price(
  //               baseAmount.currency,
  //               parsedQuoteAmount.currency,
  //               baseAmount.quotient,
  //               parsedQuoteAmount.quotient
  //             )
  //           : undefined
  //       return (invertPrice ? price?.invert() : price) ?? undefined
  //     }
  //     return undefined
  //   } else {
  //     // get the amount of quote currency
  //     return pool && token0 ? pool.priceOf(token0) : undefined
  //   }
  // }, [noLiquidity, startPriceTypedValue, invertPrice, token1, token0, pool])

  const fewPrice: Price<Token, Token> | undefined = useMemo(() => {
    // if no liquidity use typed value
    if (noLiquidity) {
      const parsedQuoteAmount = tryParseCurrencyAmount(startPriceTypedValue, invertFewPrice ? fewToken0 : fewToken1)
      console.log(parsedQuoteAmount, 'parsedQuoteAmount')
      if (parsedQuoteAmount && fewToken0 && fewToken1) {
        const baseAmount = tryParseCurrencyAmount('1', invertFewPrice ? fewToken1 : fewToken0)
        const price =
          baseAmount && parsedQuoteAmount
            ? new Price(
                baseAmount.currency,
                parsedQuoteAmount.currency,
                baseAmount.quotient,
                parsedQuoteAmount.quotient
              )
            : undefined
        return (invertFewPrice ? price?.invert() : price) ?? undefined
      }
      return undefined
    } else {
      // get the amount of quote currency
      return fewPool && fewToken0 ? fewPool.priceOf(fewToken0) : undefined
    }
  }, [noLiquidity, startPriceTypedValue, invertFewPrice, fewToken1, fewToken0, fewPool])

  // check for invalid price input (converts to invalid ratio)
  // const invalidPrice = useMemo(() => {
  //   const sqrtRatioX96 = price ? encodeSqrtRatioX96(price.numerator, price.denominator) : undefined
  //   return (
  //     price &&
  //     sqrtRatioX96 &&
  //     !(
  //       JSBI.greaterThanOrEqual(sqrtRatioX96, TickMath.MIN_SQRT_RATIO) &&
  //       JSBI.lessThan(sqrtRatioX96, TickMath.MAX_SQRT_RATIO)
  //     )
  //   )
  // }, [price])

  const invalidFewPrice = useMemo(() => {
    const sqrtRatioX96 = fewPrice ? encodeSqrtRatioX96(fewPrice.numerator, fewPrice.denominator) : undefined
    return (
      fewPrice &&
      sqrtRatioX96 &&
      !(
        JSBI.greaterThanOrEqual(sqrtRatioX96, TickMath.MIN_SQRT_RATIO) &&
        JSBI.lessThan(sqrtRatioX96, TickMath.MAX_SQRT_RATIO)
      )
    )
  }, [fewPrice])

  // used for ratio calculation when pool not initialized
  // const mockPool = useMemo(() => {
  //   if (tokenA && tokenB && feeAmount && price && !invalidPrice) {
  //     const currentTick = priceToClosestTick(price)
  //     const currentSqrt = TickMath.getSqrtRatioAtTick(currentTick)
  //     return new Pool(tokenA, tokenB, feeAmount, currentSqrt, JSBI.BigInt(0), currentTick, [])
  //   } else {
  //     return undefined
  //   }
  // }, [feeAmount, invalidPrice, price, tokenA, tokenB])

  const mockFewPool = useMemo(() => {
    if (fewTokenA && fewTokenB && feeAmount && fewPrice && !invalidFewPrice) {
      const currentTick = priceToClosestTick(fewPrice)
      const currentSqrt = TickMath.getSqrtRatioAtTick(currentTick)
      return new Pool(fewTokenA, fewTokenB, feeAmount, currentSqrt, JSBI.BigInt(0), currentTick, [])
    } else {
      return undefined
    }
  }, [feeAmount, invalidFewPrice, fewPrice, fewTokenA, fewTokenB])

  // if pool exists use it, if not use the mock pool
  console.log(fewPool, 'fewPool', mockFewPool, 'mockFewPool')
  const poolForPosition: Pool | undefined = fewPool ?? mockFewPool

  // lower and upper limits in the tick space for `feeAmoun<Trans>
  const tickSpaceLimits = useMemo(
    () => ({
      [Bound.LOWER]: feeAmount ? nearestUsableTick(TickMath.MIN_TICK, TICK_SPACINGS[feeAmount]) : undefined,
      [Bound.UPPER]: feeAmount ? nearestUsableTick(TickMath.MAX_TICK, TICK_SPACINGS[feeAmount]) : undefined,
    }),
    [feeAmount]
  )

  // parse typed range values and determine closest ticks
  // lower should always be a smaller tick
  const ticks = useMemo(() => {
    return {
      [Bound.LOWER]:
        typeof existingPosition?.tickLower === 'number'
          ? existingPosition.tickLower
          : (invertPrice && typeof rightRangeTypedValue === 'boolean') ||
            (!invertPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.LOWER]
          : invertPrice
          ? tryParseTick(token1, token0, feeAmount, rightRangeTypedValue.toString())
          : tryParseTick(token0, token1, feeAmount, leftRangeTypedValue.toString()),
      [Bound.UPPER]:
        typeof existingPosition?.tickUpper === 'number'
          ? existingPosition.tickUpper
          : (!invertPrice && typeof rightRangeTypedValue === 'boolean') ||
            (invertPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.UPPER]
          : invertPrice
          ? tryParseTick(token1, token0, feeAmount, leftRangeTypedValue.toString())
          : tryParseTick(token0, token1, feeAmount, rightRangeTypedValue.toString()),
    }
  }, [
    existingPosition,
    feeAmount,
    invertPrice,
    leftRangeTypedValue,
    rightRangeTypedValue,
    token0,
    token1,
    tickSpaceLimits,
  ])

  const fewTicks = useMemo(() => {
    return {
      [Bound.LOWER]:
        typeof existingPosition?.tickLower === 'number'
          ? existingPosition.tickLower
          : (invertFewPrice && typeof rightRangeTypedValue === 'boolean') ||
            (!invertFewPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.LOWER]
          : invertFewPrice
          ? tryParseTick(fewToken1, fewToken0, feeAmount, rightRangeTypedValue.toString())
          : tryParseTick(fewToken0, fewToken1, feeAmount, leftRangeTypedValue.toString()),
      [Bound.UPPER]:
        typeof existingPosition?.tickUpper === 'number'
          ? existingPosition.tickUpper
          : (!invertFewPrice && typeof rightRangeTypedValue === 'boolean') ||
            (invertFewPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.UPPER]
          : invertFewPrice
          ? tryParseTick(fewToken1, fewToken0, feeAmount, leftRangeTypedValue.toString())
          : tryParseTick(fewToken0, fewToken1, feeAmount, rightRangeTypedValue.toString()),
    }
  }, [
    existingPosition?.tickLower,
    existingPosition?.tickUpper,
    invertFewPrice,
    rightRangeTypedValue,
    leftRangeTypedValue,
    tickSpaceLimits,
    fewToken1,
    fewToken0,
    feeAmount,
  ])

  // const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks || {}
  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = fewTicks || {}

  // specifies whether the lower and upper ticks is at the exteme bounds
  const ticksAtLimit = useMemo(
    () => ({
      [Bound.LOWER]: feeAmount && tickLower === tickSpaceLimits.LOWER,
      [Bound.UPPER]: feeAmount && tickUpper === tickSpaceLimits.UPPER,
    }),
    [tickSpaceLimits, tickLower, tickUpper, feeAmount]
  )

  // mark invalid range
  const invalidRange = Boolean(typeof tickLower === 'number' && typeof tickUpper === 'number' && tickLower >= tickUpper)

  const pricesAtLimit = useMemo(() => {
    return {
      [Bound.LOWER]: getTickToPrice(token0, token1, tickSpaceLimits.LOWER),
      [Bound.UPPER]: getTickToPrice(token0, token1, tickSpaceLimits.UPPER),
    }
  }, [token0, token1, tickSpaceLimits.LOWER, tickSpaceLimits.UPPER])

  // always returns the price with 0 as base token
  const pricesAtTicks = useMemo(() => {
    return {
      [Bound.LOWER]: getTickToPrice(token0, token1, ticks[Bound.LOWER]),
      [Bound.UPPER]: getTickToPrice(token0, token1, ticks[Bound.UPPER]),
    }
  }, [token0, token1, ticks])

  const fewPricesAtTicks = useMemo(() => {
    return {
      [Bound.LOWER]: getTickToPrice(fewToken0, fewToken1, fewTicks[Bound.LOWER]),
      [Bound.UPPER]: getTickToPrice(fewToken0, fewToken1, fewTicks[Bound.UPPER]),
    }
  }, [fewToken0, fewToken1, fewTicks])
  const { [Bound.LOWER]: lowerFewPrice, [Bound.UPPER]: upperFewPrice } = fewPricesAtTicks

  // liquidity range warning
  const outOfRange = Boolean(
    !invalidRange &&
      fewPrice &&
      lowerFewPrice &&
      upperFewPrice &&
      (fewPrice.lessThan(lowerFewPrice) || fewPrice.greaterThan(upperFewPrice))
  )

  // amounts
  const independentAmount: CurrencyAmount<Currency> | undefined = tryParseCurrencyAmount(
    typedValue,
    increaseCurrencies[independentField]
  )
  const fewIndependentAmount: CurrencyAmount<Currency> | undefined = tryParseCurrencyAmount(
    typedValue,
    fewCurrencies[independentField]
  )

  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    // we wrap the increaseCurrencies just to get the price in terms of the other token
    const wrappedIndependentAmount = independentAmount?.wrapped
    const dependentCurrency =
      dependentField === Field.CURRENCY_B ? increaseCurrencies[Field.CURRENCY_B] : increaseCurrencies[Field.CURRENCY_A]
    if (
      fewToken0 &&
      fewToken1 &&
      fewTokenA &&
      fewTokenB &&
      independentAmount &&
      fewIndependentAmount &&
      wrappedIndependentAmount &&
      typeof tickLower === 'number' &&
      typeof tickUpper === 'number' &&
      poolForPosition
    ) {
      // if price is out of range or invalid range - return 0 (single deposit will be independent)
      if (outOfRange || invalidRange) {
        return undefined
      }

      console.log(
        poolForPosition,
        independentAmount.toSignificant(),
        wrappedIndependentAmount.currency,
        'independentAmount.toSignificant()'
      )
      const fewWrappedIndependentToken = getFewTokenFromOriginalToken(
        wrappedIndependentAmount.wrapped.currency,
        independentAmount?.wrapped?.currency.chainId
      )

      const position: Position | undefined = fewWrappedIndependentToken.equals(poolForPosition.token0)
        ? Position.fromAmount0({
            pool: poolForPosition,
            tickLower,
            tickUpper,
            amount0: fewIndependentAmount.quotient,
            useFullPrecision: true, // we want full precision for the theoretical position
          })
        : Position.fromAmount1({
            pool: poolForPosition,
            tickLower,
            tickUpper,
            amount1: fewIndependentAmount.quotient,
          })

      const dependentTokenAmount =
        fewWrappedIndependentToken.equals(poolForPosition.token0) && poolForPosition.token0.equals(fewToken0)
          ? position.amount1
          : position.amount0
      return dependentCurrency && CurrencyAmount.fromRawAmount(dependentCurrency, dependentTokenAmount.quotient)
    }

    return undefined
  }, [
    independentAmount,
    dependentField,
    increaseCurrencies,
    fewToken0,
    fewToken1,
    fewTokenA,
    fewTokenB,
    fewIndependentAmount,
    tickLower,
    tickUpper,
    poolForPosition,
    outOfRange,
    invalidRange,
  ])

  console.log(independentAmount?.toSignificant(), dependentAmount?.toSignificant(), '333')
  const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = useMemo(() => {
    return {
      [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount,
    }
  }, [dependentAmount, independentAmount, independentField])

  // single deposit only if price is out of range
  const deposit0Disabled = Boolean(
    typeof tickUpper === 'number' && poolForPosition && poolForPosition.tickCurrent >= tickUpper
  )
  const deposit1Disabled = Boolean(
    typeof tickLower === 'number' && poolForPosition && poolForPosition.tickCurrent <= tickLower
  )

  // sorted for token order
  // const depositADisabled =
  //   invalidRange ||
  //   Boolean(
  //     (deposit0Disabled && poolForPosition && tokenA && poolForPosition.token0.equals(tokenA)) ||
  //       (deposit1Disabled && poolForPosition && tokenA && poolForPosition.token1.equals(tokenA))
  //   )
  // const depositBDisabled =
  //   invalidRange ||
  //   Boolean(
  //     (deposit0Disabled && poolForPosition && tokenB && poolForPosition.token0.equals(tokenB)) ||
  //       (deposit1Disabled && poolForPosition && tokenB && poolForPosition.token1.equals(tokenB))
  //   )
  const depositADisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled && poolForPosition && fewTokenA && poolForPosition.token0.equals(fewTokenA)) ||
        (deposit1Disabled && poolForPosition && fewTokenA && poolForPosition.token1.equals(fewTokenA))
    )
  const depositBDisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled && poolForPosition && fewTokenB && poolForPosition.token0.equals(fewTokenB)) ||
        (deposit1Disabled && poolForPosition && fewTokenB && poolForPosition.token1.equals(fewTokenB))
    )

  // create position entity based on users selection
  const position: Position | undefined = useMemo(() => {
    if (
      !poolForPosition ||
      (!tokenA && !fewTokenA) ||
      (!tokenB && !fewTokenB) ||
      typeof tickLower !== 'number' ||
      typeof tickUpper !== 'number' ||
      invalidRange
    ) {
      return undefined
    }

    // mark as 0 if disabled because out of range
    const amount0 = !deposit0Disabled
      ? parsedAmounts?.[fewTokenA?.equals(poolForPosition.token0) ? Field.CURRENCY_A : Field.CURRENCY_B]?.quotient
      : BIG_INT_ZERO
    const amount1 = !deposit1Disabled
      ? parsedAmounts?.[fewTokenA?.equals(poolForPosition.token0) ? Field.CURRENCY_B : Field.CURRENCY_A]?.quotient
      : BIG_INT_ZERO

    if (amount0 !== undefined && amount1 !== undefined) {
      return Position.fromAmounts({
        pool: poolForPosition,
        tickLower,
        tickUpper,
        amount0,
        amount1,
        useFullPrecision: true, // we want full precision for the theoretical position
      })
    } else {
      return undefined
    }
  }, [
    poolForPosition,
    tokenA,
    fewTokenA,
    tokenB,
    fewTokenB,
    tickLower,
    tickUpper,
    invalidRange,
    deposit0Disabled,
    deposit1Disabled,
    parsedAmounts,
  ])

  let errorMessage: ReactNode | undefined
  if (!account) {
    errorMessage = <Trans>Connect Wallet</Trans>
  }

  if (poolState === PoolState.INVALID) {
    errorMessage = errorMessage ?? <Trans>Invalid pair</Trans>
  }

  // if (invalidPrice) {
  //   errorMessage = errorMessage ?? <Trans>Invalid price input</Trans>
  // }

  if (invalidFewPrice) {
    errorMessage = errorMessage ?? <Trans>Invalid price input</Trans>
  }

  if (
    (!parsedAmounts[Field.CURRENCY_A] && !depositADisabled) ||
    (!parsedAmounts[Field.CURRENCY_B] && !depositBDisabled)
  ) {
    errorMessage = errorMessage ?? <Trans>Enter an amount</Trans>
  }

  const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts

  if (currencyAAmount && currencyBalances?.[Field.CURRENCY_A]?.lessThan(currencyAAmount)) {
    errorMessage = <Trans>Insufficient {increaseCurrencies[Field.CURRENCY_A]?.symbol} balance</Trans>
  }

  if (currencyBAmount && currencyBalances?.[Field.CURRENCY_B]?.lessThan(currencyBAmount)) {
    errorMessage = <Trans>Insufficient {increaseCurrencies[Field.CURRENCY_B]?.symbol} balance</Trans>
  }

  const invalidPool = fewPoolState === PoolState.INVALID

  return {
    dependentField,
    currencies,
    originCurrencies,
    pool,
    poolState,
    currencyBalances,
    parsedAmounts,
    ticks,
    // price,
    fewPrice,
    pricesAtTicks,
    pricesAtLimit,
    position,
    noLiquidity,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    invertFewPrice,
    ticksAtLimit,
  }
}

export function useRangeHopCallbacks(
  baseCurrency: Currency | undefined,
  quoteCurrency: Currency | undefined,
  feeAmount: FeeAmount | undefined,
  tickLower: number | undefined,
  tickUpper: number | undefined,
  pool?: Pool | undefined | null
) {
  const dispatch = useAppDispatch()

  const baseToken = useMemo(() => baseCurrency?.wrapped, [baseCurrency])
  const quoteToken = useMemo(() => quoteCurrency?.wrapped, [quoteCurrency])

  const getDecrementLower = useCallback(() => {
    if (baseToken && quoteToken && typeof tickLower === 'number' && feeAmount) {
      const newPrice = tickToPrice(baseToken, quoteToken, tickLower - TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    // use pool current tick as starting tick if we have pool but no tick input
    if (!(typeof tickLower === 'number') && baseToken && quoteToken && feeAmount && pool) {
      const newPrice = tickToPrice(baseToken, quoteToken, pool.tickCurrent - TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    return ''
  }, [baseToken, quoteToken, tickLower, feeAmount, pool])

  const getIncrementLower = useCallback(() => {
    if (baseToken && quoteToken && typeof tickLower === 'number' && feeAmount) {
      const newPrice = tickToPrice(baseToken, quoteToken, tickLower + TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    // use pool current tick as starting tick if we have pool but no tick input
    if (!(typeof tickLower === 'number') && baseToken && quoteToken && feeAmount && pool) {
      const newPrice = tickToPrice(baseToken, quoteToken, pool.tickCurrent + TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    return ''
  }, [baseToken, quoteToken, tickLower, feeAmount, pool])

  const getDecrementUpper = useCallback(() => {
    if (baseToken && quoteToken && typeof tickUpper === 'number' && feeAmount) {
      const newPrice = tickToPrice(baseToken, quoteToken, tickUpper - TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    // use pool current tick as starting tick if we have pool but no tick input
    if (!(typeof tickUpper === 'number') && baseToken && quoteToken && feeAmount && pool) {
      const newPrice = tickToPrice(baseToken, quoteToken, pool.tickCurrent - TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    return ''
  }, [baseToken, quoteToken, tickUpper, feeAmount, pool])

  const getIncrementUpper = useCallback(() => {
    if (baseToken && quoteToken && typeof tickUpper === 'number' && feeAmount) {
      const newPrice = tickToPrice(baseToken, quoteToken, tickUpper + TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    // use pool current tick as starting tick if we have pool but no tick input
    if (!(typeof tickUpper === 'number') && baseToken && quoteToken && feeAmount && pool) {
      const newPrice = tickToPrice(baseToken, quoteToken, pool.tickCurrent + TICK_SPACINGS[feeAmount])
      return newPrice.toSignificant(5, undefined, Rounding.ROUND_UP)
    }
    return ''
  }, [baseToken, quoteToken, tickUpper, feeAmount, pool])

  const getSetFullRange = useCallback(() => {
    dispatch(setFullRange())
  }, [dispatch])

  return { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper, getSetFullRange }
}
